import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, List, Select, Segmented, Spin, notification } from "antd";
import {
  WhatsAppOutlined,
  PhoneOutlined,
  MessageOutlined,
  MailOutlined,
  CheckOutlined,
  CloseOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  fetchActivities,
  fetchStats,
  fetchRules,
  approveActivity,
  rejectActivity,
} from "./service";
import {
  STATUS_COLORS,
  STATUS_LABELS,
  formatRelative,
} from "./constants";
import styles from "./style.module.scss";
import { BrandTag } from "pages/SurveyManagement/phoneTriggerShared";

const POLL_MS = 15000;
const IN_PROGRESS = ["pending_approval", "queued", "sent", "in_progress", "scheduled"];

const fmtSeconds = (s) => {
  if (s === null || s === undefined) return "—";
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  return `${Math.round(s / 3600)}h`;
};

const fmtPct = (v) => `${Math.round((v || 0) * 100)}%`;

const sinceIso = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
};

const maskCustomer = (item) => {
  const cref = item.customer_ref || {};
  if (cref.name) return cref.name;
  if (cref.phone) {
    const p = String(cref.phone);
    return p.length > 4 ? `···${p.slice(-4)}` : p;
  }
  if (cref.email) {
    const e = String(cref.email);
    const at = e.indexOf("@");
    return at > 1 ? `${e[0]}···${e.slice(at)}` : e;
  }
  return `Customer #${String(item.id || "0000").slice(-4)}`;
};

const statusDisplay = (status, t) => {
  const map = {
    sent: t("sonic.status_sent_short"),
    delivered: t("sonic.status_delivered_short"),
    replied: t("sonic.status_replied_short"),
    failed: t("sonic.status_failed_short"),
    pending_approval: t("sonic.status_pending_short"),
    scheduled: t("sonic.status_scheduled_short"),
  };
  return map[status] || STATUS_LABELS[status] || status;
};

const ChannelIcon = ({ channel }) => {
  if (channel === "voice_call")
    return <PhoneOutlined className={styles.channelIconVoice} />;
  if (channel === "sms")
    return <MessageOutlined className={styles.channelIconSms} />;
  if (channel === "email")
    return <MailOutlined className={styles.channelIconEmail} />;
  return <WhatsAppOutlined className={styles.channelIconWa} />;
};

const channelLabel = (channel, t) => {
  if (channel === "voice_call") return t("sonic.channel_voice");
  if (channel === "sms") return "SMS";
  if (channel === "email") return "Email";
  return t("sonic.channel_whatsapp");
};

/** ``readOnly``: the Engage plan has ended; insights are shown without the set-up actions. */
const InsightsTab = ({
  active,
  onManageRules,
  onCreateRule,
  onTryVoiceAgent,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [actioningId, setActioningId] = useState(null);
  const pollRef = useRef(null);

  const [segment, setSegment] = useState("all");
  const [channel, setChannel] = useState("all");
  const [days, setDays] = useState(7);
  const [feedView, setFeedView] = useState("all");
  const [pendingTotal, setPendingTotal] = useState(0);

  const queryParams = useMemo(() => {
    const since = sinceIso(days);
    const base = {
      limit: 50,
      since,
      segment: segment !== "all" ? segment : undefined,
      channel: channel !== "all" ? channel : undefined,
    };
    if (feedView === "pending") {
      base.status = "pending_approval";
    }
    return base;
  }, [segment, channel, days, feedView]);

  const load = useCallback(
    async (showSpinner) => {
      if (showSpinner) setLoading(true);
      const statsParams = {
        days,
        segment: segment !== "all" ? segment : undefined,
        channel: channel !== "all" ? channel : undefined,
        since: sinceIso(days),
      };
      const [statsRes, actRes, pendingRes] = await Promise.all([
        fetchStats(statsParams),
        fetchActivities(queryParams),
        fetchActivities({ status: "pending_approval", limit: 200, since: sinceIso(365) }),
      ]);
      if (statsRes && !statsRes.error) setStats(statsRes);
      if (actRes && !actRes.error) setActivities(actRes.activities || []);
      if (pendingRes && !pendingRes.error) {
        setPendingTotal((pendingRes.activities || []).length);
      }
      if (showSpinner) setLoading(false);
    },
    [queryParams, days, segment, channel]
  );

  useEffect(() => {
    if (!active) return undefined;
    load(true);
    return () => {};
  }, [active, load]);

  useEffect(() => {
    if (!active) return undefined;
    const hasLive = activities.some((a) => IN_PROGRESS.includes(a.status));
    if (!hasLive) return undefined;
    pollRef.current = setInterval(() => load(false), POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [active, activities, load]);

  const handleApprove = async (id) => {
    setActioningId(id);
    const res = await approveActivity(id);
    setActioningId(null);
    if (res && !res.error) {
      notification.success({ message: t("sonic.activity_approved") });
      load(false);
    } else {
      notification.error({ message: (res && res.error) || t("sonic.action_failed") });
    }
  };

  const handleReject = async (id) => {
    setActioningId(id);
    const res = await rejectActivity(id);
    setActioningId(null);
    if (res && !res.error) {
      notification.success({ message: t("sonic.activity_rejected") });
      load(false);
    } else {
      notification.error({ message: (res && res.error) || t("sonic.action_failed") });
    }
  };

  const goToRule = (ruleId) => {
    if (!ruleId) return;
    history.push(`/console/sonic?tab=rules&rule=${ruleId}`);
  };

  const hasActivity =
    stats &&
    (stats.messages_sent_7d > 0 ||
      stats.voice_calls_7d > 0 ||
      (stats.open_rate || 0) > 0);

  const showEmptyMetrics = !hasActivity && activities.length === 0;

  const statCards = [
    {
      label: t("sonic.stat_messages_sent"),
      value: showEmptyMetrics ? "—" : stats ? stats.messages_sent_7d : "—",
    },
    {
      label: t("sonic.stat_open_rate"),
      value: showEmptyMetrics ? "—" : stats ? fmtPct(stats.open_rate) : "—",
    },
    {
      label: t("sonic.stat_ctr"),
      value: showEmptyMetrics ? "—" : stats ? fmtPct(stats.click_through_rate) : "—",
    },
    {
      label: t("sonic.stat_avg_response"),
      value: showEmptyMetrics ? "—" : stats ? fmtSeconds(stats.avg_response_seconds) : "—",
    },
    {
      label: t("sonic.stat_voice_calls"),
      value: showEmptyMetrics ? "—" : stats ? stats.voice_calls_7d : "—",
    },
  ];

  if (!active) return null;

  return (
    <div className={styles.tabInner}>
      <div className={styles.sonicPanel}>
        <div className={styles.activityFilterBar}>
          <Select
            value={segment}
            onChange={setSegment}
            style={{ width: 140 }}
            options={[
              { value: "all", label: t("sonic.filter_segment_all") },
              { value: "vip", label: t("sonic.filter_segment_vip") },
              { value: "standard", label: t("sonic.filter_segment_standard") },
            ]}
          />
          <Select
            value={channel}
            onChange={setChannel}
            style={{ width: 150 }}
            options={[
              { value: "all", label: t("sonic.filter_channel_all") },
              { value: "whatsapp", label: t("sonic.channel_whatsapp") },
              { value: "voice_call", label: t("sonic.channel_voice") },
              { value: "sms", label: "SMS" },
            ]}
          />
          <Select
            value={days}
            onChange={setDays}
            style={{ width: 120 }}
            options={[
              { value: 7, label: t("sonic.filter_days_7") },
              { value: 30, label: t("sonic.filter_days_30") },
            ]}
          />
        </div>

        <div className={styles.statsRow}>
          {statCards.map((c) => (
            <div key={c.label} className={styles.statCard}>
              <span className={styles.statValue}>{c.value}</span>
              <span className={styles.statLabel}>{c.label}</span>
            </div>
          ))}
        </div>

        {showEmptyMetrics && (
          <div className={styles.activityBanner}>
            <span>{t("sonic.activity_empty_banner")}</span>
            <Button type="link" size="small" onClick={onManageRules}>
              {t("sonic.check_rules_link")}
            </Button>
          </div>
        )}

        <div className={styles.sectionHeader}>
          <h3>{t("sonic.tab_insights")}</h3>
          <div className={styles.feedHeaderActions}>
            <Segmented
              size="small"
              value={feedView}
              onChange={setFeedView}
              options={[
                { value: "all", label: t("sonic.feed_all") },
                {
                  value: "pending",
                  label:
                    pendingTotal > 0
                      ? `${t("sonic.feed_pending")} (${pendingTotal})`
                      : t("sonic.feed_pending"),
                },
              ]}
            />
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => load(true)}
            >
              {t("sonic.refresh")}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className={styles.centeredSpin}>
            <Spin />
          </div>
        ) : activities.length === 0 ? (
          <div className={styles.emptyState}>
            <ThunderboltOutlined className={styles.emptyStateIcon} />
            <h3>{t("sonic.activity_empty_title")}</h3>
            <p>{t("sonic.activity_empty_body")}</p>
            {readOnly ? null : (
              <div className={styles.emptyStateActions}>
                <Button type="primary" onClick={onCreateRule}>
                  {t("sonic.activity_cta_rule")}
                </Button>
                <Button onClick={onTryVoiceAgent}>{t("sonic.activity_cta_test")}</Button>
              </div>
            )}
          </div>
        ) : (
          <List
            className={styles.feedList}
            dataSource={activities}
            rowKey="id"
            renderItem={(item) => (
              <List.Item
                className={styles.feedItem}
                actions={
                  item.status === "pending_approval"
                    ? [
                        <Button
                          key="approve"
                          type="primary"
                          size="small"
                          icon={<CheckOutlined />}
                          loading={actioningId === item.id}
                          onClick={() => handleApprove(item.id)}
                        >
                          {t("sonic.approve")}
                        </Button>,
                        <Button
                          key="reject"
                          danger
                          size="small"
                          icon={<CloseOutlined />}
                          loading={actioningId === item.id}
                          onClick={() => handleReject(item.id)}
                        >
                          {t("sonic.reject")}
                        </Button>,
                      ]
                    : undefined
                }
              >
                <div className={styles.feedRow}>
                  <span className={styles.feedColTime}>
                    {formatRelative(item.created_at)}
                  </span>
                  <span className={styles.feedColCustomer}>
                    <ChannelIcon channel={item.channel} />
                    {maskCustomer(item)}
                  </span>
                  <span className={styles.feedColRule}>
                    {item.rule_name ? (
                      <Button
                        type="link"
                        size="small"
                        className={styles.ruleLinkBtn}
                        onClick={() => goToRule(item.rule_id)}
                      >
                        {item.rule_name}
                      </Button>
                    ) : (
                      <span className={styles.feedMuted}>
                        {item.trigger_source || t("sonic.manual_trigger")}
                      </span>
                    )}
                  </span>
                  <span className={styles.feedColChannel}>
                    {channelLabel(item.channel, t)}
                  </span>
                  <span className={styles.feedColStatus}>
                    <BrandTag tone={STATUS_COLORS[item.status]}>
                      {statusDisplay(item.status, t)}
                    </BrandTag>
                  </span>
                  <span className={styles.feedColReply}>
                    {item.reply_text ? (
                      <span className={styles.feedReply} title={item.reply_text}>
                        {item.reply_text.slice(0, 40)}
                        {item.reply_text.length > 40 ? "…" : ""}
                      </span>
                    ) : (
                      <span className={styles.feedMuted}>—</span>
                    )}
                  </span>
                </div>
              </List.Item>
            )}
          />
        )}
      </div>
    </div>
  );
};

export default InsightsTab;
